import { Typography } from "@mui/material";
import React from "react";

interface LegalDocumentContentProps {
  type: "privacy" | "terms";
}

/**
 * Shared legal document body used by Privacy Policy and Terms & Conditions.
 * Keeps spacing, black text, and bullet structure consistent.
 */
const LegalDocumentContent: React.FC<LegalDocumentContentProps> = ({
  type,
}) => {
  if (type === "privacy") {
    return (
      <div className="space-y-4" style={{ color: "#000" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Cellera Privacy Policy
        </Typography>
        <Typography variant="body2">
          <strong>Effective Date:</strong> [Insert Date]
          <br />
          <strong>Last Updated:</strong> [Insert Date]
        </Typography>
        <Typography variant="body2">
          Cellera Inc. (&quot;Cellera,&quot; &quot;we,&quot; &quot;us,&quot; or
          &quot;our&quot;) operates the Cellera consumer app, website, and
          related services (collectively, the &quot;Services&quot;). This
          Privacy Policy describes how we collect, use, share, store, and
          protect information you provide when you use our Services,
          particularly health, lab, wearable, lifestyle, and AI-assisted data.
          It also describes your rights and choices under applicable laws.
        </Typography>
        <Typography variant="body2">
          By accessing or using our Services, you (&quot;you,&quot;
          &quot;your,&quot; &quot;User&quot;) agree to the collection and use of
          information in accordance with this Policy. If you do not agree, do
          not use the Services.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          1. What Information We Collect
        </Typography>
        <Typography variant="body2">
          The types of information we collect depend on how you use the Services
          and what permissions you grant. Below are detailed categories:
        </Typography>

        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          a) Personal & Identifying Information
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Name, email address, phone number, physical address
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Account credentials (username, password)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Payment information (e.g., credit/debit card, billing address). We
              do not store full card numbers or sensitive payment credentials on
              our servers. All financial transactions are processed through a
              third-party payment gateway (currently Stripe), which is PCI-DSS
              compliant and securely handles your payment details on our behalf.
            </Typography>
          </li>
        </ul>

        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          b) Health, Clinical & Biometric Data (&quot;Healthcare
          Information&quot;)
        </Typography>
        <Typography variant="body2">
          Collected only if you provide access or submit it, or via connected
          devices:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Wearable data: heart rate, HRV, respiratory rate, activity
              (type/duration), sleep stages, glucose, SpO₂, temperature, stress
              metrics
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Lab results, diagnostics, imaging (e.g., bloodwork, genomics, MRI,
              DEXA)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Medical history, medications, prescriptions, diagnoses
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Lifestyle and behavior data: diet, supplements, mood, stress,
              sleep, female health tracking, self-reported inputs
            </Typography>
          </li>
        </ul>

        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          c) Technical & Usage Data
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Device identifiers, operating system, app version, browser type
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              IP address, geolocation (if permitted), log data, crash reports
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              App usage metrics, feature interactions
            </Typography>
          </li>
        </ul>

        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          d) Derived Data & Inferences
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Metrics, risk scores, trends, &quot;biological age,&quot; or
              similar outputs generated using your raw data
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Preferences inferred from your behavior, device usage, health
              trends
            </Typography>
          </li>
        </ul>

        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          e) Cookies & Tracking Technologies
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Cookies, pixels, web beacons for analytics, functional, essential,
              marketing purposes
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Third-party analytics, ad-tracking (with opt-outs where required)
            </Typography>
          </li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          2. Legal Basis & Consent
        </Typography>
        <Typography variant="body2">
          Depending on your location and data type, our legal basis for
          processing includes:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Your consent: for collecting and using personal & health data, for
              research, for marketing communications, AI assistant operations
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Performance of contract: to deliver the Services you sign up for
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Legal obligation: to comply with laws, regulations, HIPAA, etc.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Legitimate interests: for Service improvement, fraud prevention,
              analytics, but only where such interests do not override your
              rights/privacy (especially health data requires stronger
              safeguards)
            </Typography>
          </li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          2A. Roles & Responsibilities
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Cellera acts as a controller for your account, app operations,
              analytics, and customer support.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              When we provide services to a clinic or medical group, we may act
              as a processor for that organization.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Medical groups, labs, and pharmacies may act as independent
              controllers/covered entities, and their Notices of Privacy
              Practices or privacy policies govern their processing. Links to
              those notices will be provided where applicable.
            </Typography>
          </li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          3. How We Use Your Information
        </Typography>
        <Typography variant="body2">
          We use your information in the following ways:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              To deliver core functionality: health insights, personalized
              protocols, telehealth, diagnostics, labs, pharmacies, product
              fulfillment
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              To power and operate our AI assistant (zero-training of
              identifiable data unless you consent)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              To improve and develop Services, conduct de-identified research,
              and validate clinical outcomes
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              To determine your eligibility for particular services, tests, or
              protocols
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              To communicate with you: account notices, health alerts, lab
              results, reminders, customer support
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              To send marketing/promotional content (with opt-out)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              To prevent fraud, manage security, and comply with regulations
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" sx={{ mt: 2 }}>
          De-identification Authorization. We may also de-identify your personal
          data and combine it with other de-identified data to create
          aggregated, anonymized information. By de-identifying this data, you
          are contributing to our mission to advance health and human
          performance. This anonymized data is used for scientific research, to
          improve the accuracy of our AI assistant, and to develop new features
          that will help our entire community. We are committed to this process
          without ever attempting to re-identify you.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          4. Disclosure & Third-Parties
        </Typography>
        <Typography variant="body2">
          We may share your information under certain circumstances:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              With service providers (labs, pharmacies, logistics, payments,
              cloud, analytics)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              With healthcare providers you connect to
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              With research/analytics partners (only de-identified or aggregated
              data unless you consent)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              With AI model hosts when you use our assistant (minimum necessary,
              no training on identifiable data without consent)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              With regulators/legal authorities when required
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              In business transactions (merger, acquisition, sale of assets)
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Payments. To process transactions, we rely on a third-party payment
          gateway (currently Stripe). Stripe processes your payment credentials
          directly, and we only receive limited information necessary to confirm
          the transaction (e.g., payment confirmation, billing details).
          Stripe&apos;s own privacy policy and security practices apply in
          addition to this Privacy Policy.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Independent Labs & Pharmacies. Laboratory testing and pharmacy
          fulfillment may be performed by independent third parties. When they
          process your data, their privacy policies and terms apply to their
          processing activities, and they may be independent controllers of your
          data.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Advertising Boundaries: We do not use health data for targeted ads or
          profiling, and we do not share health/biometric data with ad networks
          or social media partners.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          5. Data Storage, Security & Encryption
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Data is encrypted in transit (TLS) and at rest
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              End-to-end encryption for certain synced/backed-up health data
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Role-based internal access, least privilege, audit logging,
              periodic access reviews
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Device-level security (PIN, Face ID, Touch ID) enforced
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Regular third-party security testing, incident response protocols
            </Typography>
          </li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          6. Data Retention
        </Typography>
        <Typography variant="body2">
          We retain data only as long as needed, subject to law. Typical
          retention periods:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Raw wearable streams: 24–36 months
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Derived insights/protocols: 36 months
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Telehealth/clinical visits: 24 months or longer if legally
              required
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              De-identified research data: until study end + 5 years
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              AI assistant chat: ~12 months unless deleted sooner by you
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Financial and transaction records (e.g., invoices, receipts,
              billing details): retained for up to 7 years to comply with tax,
              accounting, and financial reporting obligations.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" sx={{ mt: 2 }}>
          After retention, data is securely deleted or anonymized.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          7. Your Privacy Rights & Controls
        </Typography>
        <Typography variant="body2">
          Depending on your jurisdiction, you may have rights that include:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Accessing and obtaining a copy of your personal/health data
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Correcting or updating inaccurate/incomplete data
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Deleting data (all or by category/timeframe/provider)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Exporting data (CSV/JSON/FHIR)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Withdrawing consent for certain uses
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Restricting or contesting processing
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Portability between providers
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Appeals. If we decline to act on your request, you may appeal within
          30 days via appeals@cellera.ai. We will inform you in writing of any
          action taken or not taken in response to the appeal. If you remain
          unsatisfied, you may contact your state Attorney General or relevant
          authority.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Automated Decision-Making. We do not make decisions with legal or
          similarly significant effects solely by automated means. If we
          introduce such features, we will provide required notices and a way to
          request human review, to contest the decision, or to opt out where
          applicable.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Privacy & Sharing Dashboard: Available in-app to manage permissions,
          revoke third-party access, delete data, export data, update
          communication preferences, and clear AI assistant chats.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          8. Children&apos;s Privacy
        </Typography>
        <Typography variant="body2">
          The Services are not intended for users under 18 years. We do not
          knowingly collect data from minors. If we learn we have, we will
          delete it.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          9. U.S. State Privacy Laws & Consumer Health Data Protections
        </Typography>
        <Typography variant="body2">
          Cellera is based in the United States, and your information may be
          processed in the U.S. and other countries. We apply safeguards as
          required by applicable law.
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          State Law Rights.
        </Typography>
        <Typography variant="body2">
          If you are a resident of California, Virginia, Colorado, Connecticut,
          or Utah, you may have additional rights under CPRA, CPA, CTDPA, or
          UCPA, including:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              The right to know what categories of personal information we
              collect, disclose, and/or sell/share.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              The right to access a copy of your personal information.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              The right to correct inaccuracies in your personal information.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              The right to delete personal information, subject to exceptions.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              The right to opt out of the &quot;sale&quot; or
              &quot;sharing&quot; of personal information for targeted
              advertising or profiling.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              The right to limit the use and disclosure of sensitive personal
              information.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" sx={{ mt: 2 }}>
          To exercise these rights, submit requests via email to
          privacy@cellera.ai. We will verify your identity before processing
          your request. Authorized agents may act on your behalf where allowed
          by law.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Sensitive health/biometric data is processed only with your explicit
          consent and is never used for advertising purposes.
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Consumer Health Data (WA & NV).
        </Typography>
        <Typography variant="body2">
          If you are a resident of Washington or Nevada, we comply with state
          consumer health data laws, which include:
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Requiring separate consents for collection and sharing of consumer
              health data.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Requiring distinct authorizations for any sale of consumer health
              data.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Providing a process to appeal denied requests (see Appeals in
              Section 7).
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Prohibiting the use of geofencing around healthcare facilities to
              infer sensitive health information.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Global Privacy Control (GPC).
        </Typography>
        <Typography variant="body2">
          We recognize and honor a user-enabled Global Privacy Control (GPC)
          signal as a valid request to opt out of the &quot;sale&quot; or
          &quot;sharing&quot; of personal information, where required by law.
          This allows you to exercise certain rights automatically through your
          browser settings.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          10. Cookies, Tracking Technologies & Advertising Signals
        </Typography>
        <Typography variant="body2">
          We use a variety of tracking technologies to support and improve our
          Services.
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Types of Cookies and Technologies.
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Essential Cookies: Required for login, security, and basic
              functionality.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Functional Cookies: Remember your settings and preferences.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Analytics & Performance Cookies: Measure usage and improve app
              reliability.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Marketing Cookies: Support promotional campaigns (disabled unless
              you consent).
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Consent Management.
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Non-essential cookies are disabled by default until you provide
              consent, where required by law.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              You may withdraw consent at any time via our Privacy & Sharing
              Dashboard or device settings.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Opt-Out Mechanisms.
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              We support industry opt-out tools, such as the Network Advertising
              Initiative (NAI) and Digital Advertising Alliance (DAA).
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              We also honor GPC browser signals where required by law.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Restrictions on Health Data.
        </Typography>
        <Typography variant="body2">
          We do not use or share health, biometric, or genetic data for targeted
          advertising or cross-context behavioral advertising.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          11. AI Assistant & Telehealth
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          AI Assistant.
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Data is shared with AI model hosts only when you actively use the
              assistant.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Identifiable personal or health data is never used to train models
              without your explicit consent.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              You can review and delete your assistant history directly in-app.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Telehealth Services.
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              Use of telehealth services requires your informed consent.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Telehealth records are accessible to you and disclosed only as
              authorized or required by law.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Affiliated medical groups, labs, or pharmacies may maintain their
              own privacy policies or Notices of Privacy Practices, which apply
              in addition to this policy.
            </Typography>
          </li>
        </ul>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Limitations.
        </Typography>
        <Typography variant="body2">
          Telehealth features are not a substitute for emergency medical care.
          If you are experiencing a medical emergency, call 911.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          12. Vendor & Sub-Processor Transparency
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Engagement of Vendors.
        </Typography>
        <Typography variant="body2">
          We engage third parties to provide services such as laboratories,
          pharmacies, payment processing, cloud hosting, analytics, and
          logistics. These vendors may act as &quot;sub-processors&quot; with
          access to your personal information solely to perform their contracted
          functions.
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Sub-Processor List.
        </Typography>
        <Typography variant="body2">
          We maintain a current list of sub-processors on our website, including
          their roles and the categories of data they process.
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Notification of Changes.
        </Typography>
        <Typography variant="body2">
          We will provide advance notice before adding or replacing
          sub-processors, via our website or in-app notice.
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
          Safeguards.
        </Typography>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <Typography variant="body2">
              All vendors are contractually required to implement appropriate
              technical and organizational measures to protect your data.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Vendors are prohibited from using your data for any purpose other
              than delivering services to Cellera.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Vendors must notify us of any data breach or security incident
              without undue delay, and we will notify you where legally
              required.
            </Typography>
          </li>
        </ul>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          13. Changes to this Policy
        </Typography>
        <Typography variant="body2">
          We may update this Privacy Policy periodically. If changes materially
          affect your rights or our practices, we will provide prominent notice
          (email, app alert, or website banner) and request re-consent if
          required.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
          14. Contact Us
        </Typography>
        <Typography variant="body2">
          Cellera Inc.
          <br />
          1819 West Ave, Bay 5
          <br />
          Miami Beach, FL 33139
          <br />
          Email: privacy@cellera.ai
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Mail correspondence for privacy inquiries should be directed to
          Privacy Officer, Cellera Inc. at the address above.
        </Typography>
      </div>
    );
  }

  // Terms & Conditions
  return (
    <div className="space-y-4" style={{ color: "#000" }}>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        Cellera Terms and Conditions
      </Typography>
      <Typography variant="body2">
        <strong>Effective Date:</strong> [Insert Date]
        <br />
        <strong>Last Updated:</strong> [Insert Date]
      </Typography>
      <Typography variant="body2">
        These Terms and Conditions (&quot;Terms&quot;) are a binding agreement
        between you (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;) and
        Cellera Inc. (&quot;Cellera,&quot; &quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;). By accessing or using our consumer application,
        website, or related services (collectively, the &quot;Services&quot;),
        you agree to these Terms. If you do not agree, do not use the Services.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        1. Eligibility
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            You must be at least 18 years old to use the Services.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            By using the Services, you represent that you have the legal
            capacity to enter into this agreement.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        2. Services
      </Typography>
      <Typography variant="body2">
        Cellera provides a digital platform for wellness and health
        optimization, including:
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            Personalized health insights and longevity protocols.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Telehealth services with affiliated licensed providers.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Integration with labs, pharmacies, wearables, and diagnostics.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            An AI-powered health assistant.
          </Typography>
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Important: Cellera does not replace your relationship with a primary
        care physician or specialist. Our Services are designed for
        informational and wellness purposes only and should not be used in
        emergencies.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        3. User Responsibilities
      </Typography>
      <Typography variant="body2">You agree to:</Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            Provide accurate, current, and complete information.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Use the Services only for lawful purposes and in accordance with
            these Terms.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Keep your account credentials secure and confidential.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Not misuse or attempt to gain unauthorized access to the Services,
            systems, or networks.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Not use the Services to transmit harmful, infringing, unlawful, or
            inappropriate content.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        4. Medical Disclaimer
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            Cellera itself does not practice medicine. Licensed providers
            accessed through the Services are independent medical professionals
            and are solely responsible for the care they provide.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Any protocols, insights, or recommendations generated by the
            Services are for informational purposes only and should not
            substitute for medical advice from your physician.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            If you experience a medical emergency, call 911 (or your local
            emergency number) immediately.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        5. Privacy and Data Use
      </Typography>
      <Typography variant="body2">
        Your use of the Services is subject to our Privacy Policy, which
        explains how we collect, use, and share your information. By using the
        Services, you acknowledge and agree to our data practices as described
        in the Privacy Policy.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        6. Payment, Fees, and Refunds
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            Certain Services may require a paid subscription or one-time fees.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Prices, billing cycles, and payment methods will be disclosed at the
            time of purchase.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Except as required by law, all membership and subscription fees are
            non-refundable once services have been initiated.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Certain services, such as laboratory testing, imaging orders, or
            pharmacy fulfillment, are non-refundable once scheduled, ordered, or
            shipped.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Appointment cancellations and rescheduling must be made at least 48
            hours before the scheduled time. Missed appointments or late
            cancellations may incur additional fees.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Cellera may adjust subscription or membership fees from time to
            time. We will provide at least 10 days&apos; notice of such changes.
            Continued use of the Services after such notice constitutes
            acceptance of the new fees.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        7. Third-Party Providers & Insurance Disclaimer
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            The Services may integrate with or link to independent third-party
            providers, including laboratories, pharmacies, medical groups, or
            device makers. These providers are solely responsible for their
            services.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Cellera is not responsible for the acts, omissions, or quality of
            care of any third-party provider.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Membership or subscription to Cellera is not health insurance. We do
            not guarantee that fees or services will be reimbursed by any health
            plan or insurer.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            You are solely responsible for confirming coverage and handling
            insurance claims or reimbursement requests.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        8. Risks and Disclaimers
      </Typography>
      <Typography variant="body2">
        By using the Services, you acknowledge and agree that:
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            Health tests, lab draws, imaging, and similar procedures involve
            inherent risks, including discomfort, bruising, infection, or
            anxiety from receiving test results.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            You are responsible for consulting with your physician and following
            up on abnormal or concerning results.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            The Services are provided &quot;as is&quot; and &quot;as
            available,&quot; without warranties of any kind, whether express,
            implied, or statutory.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            To the fullest extent allowed by law, Cellera disclaims all implied
            warranties, including merchantability, fitness for a particular
            purpose, and non-infringement.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        9. Termination & Suspension
      </Typography>
      <Typography variant="body2">
        Cellera may suspend or terminate your account or membership immediately
        if you:
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">Fail to pay applicable fees;</Typography>
        </li>
        <li>
          <Typography variant="body2">Breach these Terms;</Typography>
        </li>
        <li>
          <Typography variant="body2">
            Violate laws, regulations, or the rights of others;
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Engage in abusive, disruptive, or threatening conduct; or
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Pose a security, health, or reputational risk to the Services,
            Cellera, other users, or affiliated providers.
          </Typography>
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Upon termination, your access to premium Services will end.
      </Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Your data will be handled in accordance with our Privacy Policy after
        termination, including options to access, export, or delete your data.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        10. Limitation of Liability
      </Typography>
      <Typography variant="body2">
        To the maximum extent permitted by law:
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            Cellera and its affiliates shall not be liable for any indirect,
            incidental, consequential, punitive, or special damages, including
            loss of profits, data, goodwill, or other intangible losses.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Our total liability for any claim relating to the Services shall not
            exceed the greater of: (i) the amount you paid for the Services in
            the 12 months preceding the claim, or (ii) $100.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        11. Indemnification
      </Typography>
      <Typography variant="body2">
        You agree to defend, indemnify, and hold harmless Cellera, its
        affiliates, officers, directors, employees, and contractors from any
        claims, damages, liabilities, costs, or expenses (including reasonable
        attorneys&apos; fees) arising from:
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">Your use of the Services,</Typography>
        </li>
        <li>
          <Typography variant="body2">
            Your violation of these Terms, or
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Your violation of any rights of another person or entity.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        12. Changes to the Terms
      </Typography>
      <Typography variant="body2">
        We may update these Terms from time to time. We will notify you of
        material changes through the Services or by email. Continued use of the
        Services after changes take effect constitutes your acceptance of the
        updated Terms.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        13. Governing Law & Dispute Resolution
      </Typography>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <Typography variant="body2">
            These Terms are governed by the laws of the State of Nevada, without
            regard to conflict of law rules.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Any dispute shall be resolved through binding arbitration in Clark
            County, Nevada, under the rules of the American Arbitration
            Association, unless otherwise required by law.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            You waive the right to participate in class actions.
          </Typography>
        </li>
      </ul>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        14. Entire Agreement
      </Typography>
      <Typography variant="body2">
        These Terms, together with the Privacy Policy, constitute the entire
        agreement between you and Cellera regarding the Services and supersede
        any prior agreements.
      </Typography>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>
        15. Contact
      </Typography>
      <Typography variant="body2">
        Cellera Inc.
        <br />
        1819 West Ave, Bay 5
        <br />
        Miami Beach, FL 33139
        <br />
        Email: support@cellera.ai
      </Typography>
    </div>
  );
};

export default LegalDocumentContent;
